import * as React from 'react';
import type { BasicCardItemProps } from './basic-card-list-item';
import type { Option } from './types';
import {
	Body2AccentTypography,
	CaptionAccentTypography,
	Info,
	Input,
	LoadingSpinner,
	LoadingSpinnerColor,
	LoadingSpinnerSize,
	SimpleTooltip,
	Tooltip,
} from '@components/atoms';
import { BasicCardListItem, CollapsibleCard } from '@components/organisms';
import { cn } from '@utils';

type CollapsibleListCardProps<T> = Omit<React.ComponentProps<typeof CollapsibleCard>, 'onSelect'> & {
	setSearch?: React.Dispatch<React.SetStateAction<string>>;
	placeholder?: string;
	options: Option<T>[];
	selectAllLabel?: string;
	enableSelectAll?: boolean;
	selectedValues: T[];
	isSelectAll?: boolean;
	onSelectAll?: () => void;
	isLoading?: boolean;
	onSelect: (option: Option<T>) => void;
	checkIsSelected: (option: Option<T>) => boolean;
	CardItemComponent?: React.ForwardRefExoticComponent<BasicCardItemProps<T>>;
	headerSubTitle?: string;
	lastElementRef?: React.Ref<HTMLDivElement>;
	infoTooltip?: string;
	titleTooltip?: string;
};

export const CollapsibleListCard = <T,>({
	title,
	isCollapsed,
	onHeaderClick,
	setSearch,
	placeholder,
	options,
	selectAllLabel,
	enableSelectAll,
	isSelectAll,
	selectedValues,
	onSelectAll,
	isLoading,
	onSelect,
	checkIsSelected,
	headerSubTitle,
	CardItemComponent = BasicCardListItem as React.ForwardRefExoticComponent<BasicCardItemProps<T>>,
	lastElementRef,
	infoTooltip,
	titleTooltip,
}: CollapsibleListCardProps<T>) => {
	return (
		<CollapsibleCard
			data-id="collapsible-list-card"
			title={title}
			isCollapsed={isCollapsed}
			toggleCollapse={onHeaderClick}
			headerClassName={cn('p-2', !isCollapsed && 'bg-borderPrimary')}
			className="border-b border-t border-borderPrimary p-0"
			withBorder
			headerSubComponent={
				Boolean(headerSubTitle) && (
					<CaptionAccentTypography className={isCollapsed ? 'text-tertiary' : 'text-secondary'}>
						{headerSubTitle}
					</CaptionAccentTypography>
				)
			}
			TitleComponent={
				<div className={cn('flex gap-[4px] text-secondary', isCollapsed && 'text-tertiary')}>
					<Body2AccentTypography className="text-inherit">{title}</Body2AccentTypography>
					{titleTooltip && !isCollapsed && (
						<SimpleTooltip content={titleTooltip}>
							<Info />
						</SimpleTooltip>
					)}
				</div>
			}
		>
			<div className="hflex flex-col gap-[8px] pb-1 pl-1 pr-1">
				{setSearch && (
					<Input
						data-aid="search-input"
						variant="search"
						width="100%"
						className="border-2"
						placeholder={placeholder}
						onChange={e => setSearch(e.target.value)}
					/>
				)}
				<div className="flex max-h-[280px] w-full flex-col items-center justify-items-start overflow-x-hidden overflow-y-scroll">
					{selectAllLabel && Boolean(options?.length) && enableSelectAll && (
						<BasicCardListItem
							data-id="select-all-option"
							option={{ label: selectAllLabel, value: !isSelectAll, info: infoTooltip }}
							onSelect={onSelectAll!}
							isSelected={isSelectAll}
							indeterminate={Boolean(selectedValues?.length)}
						/>
					)}
					{options.map((option, index) => {
						const isLast: boolean = index === options.length - 1;
						return (
							<CardItemComponent
								data-id="card-item"
								key={`CardItemComponent-${option.label}-${index}`}
								option={option}
								isLast={isLast}
								ref={lastElementRef}
								onSelect={onSelect}
								disabled={isSelectAll}
								isSelected={checkIsSelected(option)}
							/>
						);
					})}
					{isLoading && !options?.length && (
						<div className="flex h-full w-full items-center justify-center">
							<LoadingSpinner color={LoadingSpinnerColor.PRIMARY} size={LoadingSpinnerSize.MD} />
						</div>
					)}
				</div>
			</div>
		</CollapsibleCard>
	);
};

CollapsibleListCard.displayName = 'CollapsibleListCard';
